import React from 'react';
import { Link } from 'react-router-dom';
import { Edit, Eye, Copy, Loader2, MoreVertical, Trash2, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { TableCell, TableRow } from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { ReportForList } from '@/types/reports';
import { useAuth } from '@/contexts/auth';
import { hasPermission } from '@/utils/permissions';
import { canResendReport, canStartReportSend } from '../utils';
import { SendStatusBadge } from './SendStatusBadge';

interface ReportsTableRowProps {
  report: ReportForList;
  isCopying: boolean;
  showActions: boolean;
  onEdit: (id: string) => void;
  onOpenPreview: (id: string) => void;
  onCopy: (id: string) => void;
  onDelete: (id: string, title: string) => void;
  onStartSend: (id: string, title: string) => void;
}

export const ReportsTableRow: React.FC<ReportsTableRowProps> = ({
  report,
  isCopying,
  showActions,
  onEdit,
  onOpenPreview,
  onCopy,
  onDelete,
  onStartSend,
}) => {
  const { user } = useAuth();
  const canEditReports = hasPermission(user, 'edit_reports');
  const canCreateReports = hasPermission(user, 'create_reports');
  const canDeleteReports = hasPermission(user, 'delete_reports');
  const canSendReports = hasPermission(user, 'send_reports');
  const hasMenuActions = canEditReports || canCreateReports || canDeleteReports;
  const canStartSend = canSendReports && canStartReportSend(report.status);
  const canResend = canSendReports && canResendReport(report.status);
  const showActionsMenu = hasMenuActions || canStartSend || canResend;

  return (
    <TableRow>
      <TableCell className="max-w-[220px] min-w-[220px] px-4 py-4">
        <Link
          to={`/app/reports/${report.id}/view`}
          title={report.title}
          className="block truncate text-left font-medium text-gray-900 hover:underline"
        >
          {report.title}
        </Link>
      </TableCell>
      <TableCell className="whitespace-nowrap px-4 py-4 text-center text-gray-600">
        {report.edition ?? '—'}
      </TableCell>
      <TableCell className="px-4 py-4 text-gray-600 whitespace-nowrap">
        {format(report.createdAt, 'dd/MM/yyyy', { locale: ptBR })}
      </TableCell>
      <TableCell className="px-4 py-4 text-gray-600 whitespace-nowrap">
        {format(report.updatedAt, 'dd/MM/yyyy', { locale: ptBR })}
      </TableCell>
      <TableCell className="px-4 py-4 text-center">
        <Badge className="bg-black text-white py-[2px] px-2 whitespace-nowrap">
          {report.partner_name ?? 'Geral'}
        </Badge>
      </TableCell>
      <TableCell className="px-4 py-4 text-center text-gray-600">{report.visitor_count}</TableCell>
      <TableCell className="px-4 py-4 text-center text-gray-600">{report.view_count}</TableCell>
      <TableCell className="px-4 py-4 text-center">
        <SendStatusBadge status={report.status} />
      </TableCell>
      {showActions ? (
        <TableCell className="px-4 py-4">
          <div className="flex items-center justify-center">
            {showActionsMenu ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 min-w-8 min-h-8 p-0 shrink-0"
                    title="Ações"
                    aria-label="Abrir menu de ações"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[180px]">
                  <DropdownMenuItem onClick={() => onOpenPreview(report.id)}>
                    <Eye className="h-4 w-4 mr-2" />
                    Visualizar
                  </DropdownMenuItem>
                  {canEditReports && (
                    <DropdownMenuItem onClick={() => onEdit(report.id)}>
                      <Edit className="h-4 w-4 mr-2" />
                      Editar
                    </DropdownMenuItem>
                  )}
                  {canCreateReports && (
                    <DropdownMenuItem onClick={() => onCopy(report.id)} disabled={isCopying}>
                      {isCopying ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Copy className="h-4 w-4 mr-2" />
                      )}
                      Duplicar
                    </DropdownMenuItem>
                  )}
                  {canStartSend ? (
                    <DropdownMenuItem onClick={() => onStartSend(report.id, report.title)}>
                      <Send className="h-4 w-4 mr-2" />
                      Enviar
                    </DropdownMenuItem>
                  ) : null}
                  {canResend ? (
                    <DropdownMenuItem onClick={() => onStartSend(report.id, report.title)}>
                      <Send className="h-4 w-4 mr-2" />
                      Reenviar
                    </DropdownMenuItem>
                  ) : null}
                  {canDeleteReports ? (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => onDelete(report.id, report.title)}
                        className="text-red-600 focus:text-red-600 focus:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Excluir
                      </DropdownMenuItem>
                    </>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 min-w-8 min-h-8 p-0 shrink-0"
                title="Visualizar"
                aria-label="Visualizar report"
                onClick={() => onOpenPreview(report.id)}
              >
                <Eye className="h-4 w-4" />
              </Button>
            )}
          </div>
        </TableCell>
      ) : null}
    </TableRow>
  );
};
